import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    title: '1. Information We Collect',
    content: (
      <>
        <h3>Information you provide directly</h3>
        <ul>
          <li>Name, email address, and phone number when you contact us or request a consultation.</li>
          <li>Business name and details about your current processes, tools, and systems shared during advisory engagements.</li>
          <li>Project requirements and technical specifications shared during software development or automation projects.</li>
        </ul>
        <h3>Information collected automatically</h3>
        <ul>
          <li>IP address, browser type, and device information when you visit our website.</li>
          <li>Pages visited, time spent, and referring URLs through basic analytics.</li>
          <li>Cookies and similar technologies used for site functionality.</li>
        </ul>
      </>
    )
  },
  {
    title: '2. How We Use Your Information',
    content: (
      <ul>
        <li>Respond to your inquiries and provide requested services.</li>
        <li>Assess your current IT systems, tools, and workflows as part of our advisory services.</li>
        <li>Design, build, and maintain automation workflows and software solutions.</li>
        <li>Send project updates, invoices, and service-related communications.</li>
        <li>Improve our website, services, and client experience.</li>
      </ul>
    )
  },
  {
    title: '3. Information Sharing',
    content: (
      <>
        <p>We do not sell, rent, or trade your personal information. We may share information only:</p>
        <ul>
          <li>With your written consent.</li>
          <li>When required by law or legal process.</li>
          <li>With trusted service providers who are bound by confidentiality agreements and process data on our behalf.</li>
        </ul>
      </>
    )
  },
  {
    title: '4. Data From Automation & Advisory Engagements',
    content: (
      <>
        <p>When we build or manage automation workflows for your business:</p>
        <ul>
          <li>We access connected systems and data only as necessary to design, implement, and maintain your workflows.</li>
          <li>We do not use, copy, or retain your business data beyond delivering the agreed services.</li>
          <li>Credentials, API keys, and access tokens are stored securely and used solely for the intended integration.</li>
          <li>Upon termination of services, we deactivate access and can provide documentation of integrations upon request.</li>
        </ul>
      </>
    )
  },
  {
    title: '5. Data Security',
    content: (
      <>
        <p>We implement reasonable technical and organizational safeguards to protect your information, including:</p>
        <ul>
          <li>Encrypted storage of credentials and sensitive configuration.</li>
          <li>Access limited to team members directly involved in your project.</li>
          <li>Regular review of our internal security practices.</li>
        </ul>
        <p>No method of transmission or storage is 100% secure. If a breach occurs, we will notify affected clients promptly.</p>
      </>
    )
  },
  {
    title: '6. Cookies',
    content: (
      <p>
        Our website uses minimal cookies for basic functionality. We do not use third-party
        advertising cookies. You can disable cookies in your browser settings without affecting
        your ability to use our site.
      </p>
    )
  },
  {
    title: '7. Your Rights',
    content: (
      <>
        <p>You may:</p>
        <ul>
          <li>Request a copy of the personal information we hold about you.</li>
          <li>Ask us to correct inaccurate information.</li>
          <li>Request deletion of your personal data, subject to legal or contractual retention obligations.</li>
          <li>Opt out of non-essential communications at any time.</li>
        </ul>
        <p>
          To exercise these rights, contact us at{' '}
          <a href="mailto:info@genitsolutions.us">info@genitsolutions.us</a>.
        </p>
      </>
    )
  },
  {
    title: "8. Children's Privacy",
    content: (
      <p>
        Our services are intended for businesses and professionals. We do not knowingly collect
        information from children under 16.
      </p>
    )
  },
  {
    title: '9. International Clients',
    content: (
      <p>
        If you are located outside the United States, your information will be processed and stored
        in the U.S. By using our services, you consent to this transfer.
      </p>
    )
  },
  {
    title: '10. Changes to This Policy',
    content: (
      <p>
        We may update this Privacy Policy from time to time. Changes will be posted on this page
        with an updated revision date. We encourage you to review this page periodically.
      </p>
    )
  },
  {
    title: '11. Contact Us',
    content: (
      <p>
        Genitsolutions INC
        <br />
        Frederick, MD USA
        <br />
        Email: <a href="mailto:info@genitsolutions.us">info@genitsolutions.us</a>
      </p>
    )
  }
];

function Policy() {
  useEffect(() => {
    document.title = 'Privacy Policy — GEN IT SOLUTIONS INC';
    window.scrollTo(0, 0);

    return () => {
      document.title = 'GEN IT SOLUTIONS INC — Intelligent Systems for Ambitious Companies';
    };
  }, []);

  return (
    <section className="policy-page">
      <div className="policy-header">
        <Link className="policy-back" to="/">
          ← Back to home
        </Link>
        <span className="section-label section-label--dark">Legal</span>
        <h1>Privacy Policy</h1>
        <p className="policy-date">Last updated: July 13, 2026</p>
      </div>

      <div className="policy-intro">
        <p>
          Genitsolutions INC (“we,” “us,” or “our”) respects your privacy. This Privacy Policy
          explains how we collect, use, and protect your information when you visit genitsolutions.us
          or use our services.
        </p>
      </div>

      <div className="policy-content">
        {sections.map((section) => (
          <article className="policy-section" key={section.title}>
            <h2>{section.title}</h2>
            <div>{section.content}</div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default Policy;